import { ArrowLeft } from "@phosphor-icons/react";
import { forwardRef } from "react";
import styled from "@emotion/styled";
import { IconButton } from "@/components/primitives/Button";
import type { PageHeaderProps } from "./PageHeader.types";

interface PageHeaderOwnerState {
  sticky: boolean;
}

/* Exact compiled output of the pre-migration Tailwind utilities, including
 * the sm: responsive rules (min-width:640px). Verified against the frozen
 * baseline. */

const PageHeaderRoot = styled("div", {
  label: "AgenticPageHeader-root",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: PageHeaderOwnerState }>(({ ownerState }) => [
  {
    display: "flex",
    minHeight: "52px",
    flexDirection: "column",
    gap: ".75rem",
    borderBottomWidth: "1px",
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-border-strong) / var(--tw-border-opacity, 1))",
    "--tw-bg-opacity": "1",
    backgroundColor: "rgb(251 252 251 / var(--tw-bg-opacity, 1))",
    paddingLeft: "1.25rem",
    paddingRight: "1.25rem",
    paddingTop: ".75rem",
    paddingBottom: ".75rem",
    "@media (min-width:640px)": {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between"
    }
  },
  ownerState.sticky && {
    position: "sticky",
    top: 0,
    zIndex: 40
  }
]);

const PageHeaderLead = styled("div", {
  label: "AgenticPageHeader-lead"
})({
  display: "flex",
  minWidth: "0px",
  alignItems: "flex-start",
  gap: ".75rem"
});

const PageHeaderTitleBlock = styled("div", {
  label: "AgenticPageHeader-titleBlock"
})({
  minWidth: "0px"
});

const PageHeaderEyebrow = styled("div", {
  label: "AgenticPageHeader-eyebrow"
})({
  marginBottom: ".25rem",
  fontSize: "10.5px",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: ".08em",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-primary) / var(--tw-text-opacity, 1))"
});

const PageHeaderTitle = styled("h1", {
  label: "AgenticPageHeader-title"
})({
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  fontSize: "20px",
  fontWeight: 600,
  lineHeight: "1.5rem",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
});

const PageHeaderDescription = styled("p", {
  label: "AgenticPageHeader-description"
})({
  marginTop: ".25rem",
  maxWidth: "42rem",
  fontSize: "12.5px",
  lineHeight: "1.25rem",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
});

const PageHeaderActions = styled("div", {
  label: "AgenticPageHeader-actions"
})({
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: ".5rem",
  "@media (min-width:640px)": {
    justifyContent: "flex-end"
  }
});

export const PageHeader = forwardRef<HTMLDivElement, PageHeaderProps>(
  ({ className, title, description, eyebrow, breadcrumb, onBack, backLabel = "Go back", actions, secondaryActions, sticky, titleClassName, descriptionClassName, ...props }, ref) => (
    <PageHeaderRoot ref={ref} ownerState={{ sticky: Boolean(sticky) }} className={className} {...props}>
      <PageHeaderLead>
        {onBack && <IconButton aria-label={backLabel} icon={<ArrowLeft size={16} />} size="sm" variant="outline" onClick={onBack} />}
        <PageHeaderTitleBlock>
          {breadcrumb}
          {eyebrow && <PageHeaderEyebrow>{eyebrow}</PageHeaderEyebrow>}
          <PageHeaderTitle className={titleClassName}>{title}</PageHeaderTitle>
          {description && <PageHeaderDescription className={descriptionClassName}>{description}</PageHeaderDescription>}
        </PageHeaderTitleBlock>
      </PageHeaderLead>
      {(actions || secondaryActions) && (
        <PageHeaderActions>
          {secondaryActions}
          {actions}
        </PageHeaderActions>
      )}
    </PageHeaderRoot>
  )
);

PageHeader.displayName = "PageHeader";
